import {
  getGenres,
  getSchools,
  getSchool,
  getClasses,
  getEvents,
  getEvent,
  getOrganizer,
  reportOutdatedInfo,
  trackInteraction,
} from '@/services/DirectoryService.js';

// Turns the explore filters into API query params (empty values dropped).
const toSchoolParams = (filters = {}) => {
  const params = {};
  if (filters.genres?.length) params.genres = filters.genres.join(',');
  if (filters.day) params.day = filters.day;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.level) params.level = filters.level;
  if (filters.maxPrice) params.maxPrice = filters.maxPrice;
  if (filters.near) {
    params.near = `${filters.near.lat},${filters.near.lng}`;
    params.radiusKm = filters.radiusKm || 25;
  }
  return params;
};

// Wraps a detail request with the shared loading / error state.
const loadDetail = async (context, mutation, request) => {
  context.commit('setDetailLoading', true);
  context.commit('setDetailError', null);
  context.commit(mutation, null);
  try {
    const data = await request();
    context.commit(mutation, data);
    return data;
  } catch (err) {
    context.commit('setDetailError', err);
    return null;
  } finally {
    context.commit('setDetailLoading', false);
  }
};

// Id of the latest school / event search: when filters change quickly,
// only the last request may update the results and end the loading state.
let latestSchoolsRequest = 0;
let latestEventSearchRequest = 0;

// Events page filters: { genres, categories, from, to (Dates), near, limit }.
const toEventSearchParams = (filters = {}) => {
  const params = { limit: filters.limit || 100 };
  if (filters.genres?.length) params.genres = filters.genres.join(',');
  if (filters.categories?.length) params.category = filters.categories.join(',');
  if (filters.from) params.from = filters.from.toISOString();
  if (filters.to) params.to = filters.to.toISOString();
  if (filters.near) {
    params.near = `${filters.near.lat},${filters.near.lng}`;
    params.radiusKm = filters.radiusKm || 25;
  }
  return params;
};

export default {
  async fetchGenres(context) {
    if (context.state.genres.length) return context.state.genres;
    const response = await getGenres();
    context.commit('setGenres', response.data.genres);
    return response.data.genres;
  },

  async fetchSchools(context, filters) {
    const requestId = ++latestSchoolsRequest;
    const isLatest = () => requestId === latestSchoolsRequest;
    context.commit('setSchoolsLoading', true);
    context.commit('setSchoolsError', null);
    try {
      const response = await getSchools(toSchoolParams(filters));
      if (isLatest()) context.commit('setSchools', response.data.schools);
      return response.data.schools;
    } catch (err) {
      if (isLatest()) context.commit('setSchoolsError', err);
      return [];
    } finally {
      if (isLatest()) context.commit('setSchoolsLoading', false);
    }
  },

  async searchEvents(context, filters) {
    const requestId = ++latestEventSearchRequest;
    const isLatest = () => requestId === latestEventSearchRequest;
    context.commit('setEventSearchLoading', true);
    context.commit('setEventSearchError', null);
    try {
      const response = await getEvents(toEventSearchParams(filters));
      if (isLatest()) context.commit('setEventSearch', response.data.events);
      return response.data.events;
    } catch (err) {
      if (isLatest()) context.commit('setEventSearchError', err);
      return [];
    } finally {
      if (isLatest()) context.commit('setEventSearchLoading', false);
    }
  },

  async fetchEvents(context, { append = false, ...params } = {}) {
    context.commit('setEventsLoading', true);
    try {
      const response = await getEvents(params);
      const events = append
        ? [...context.state.events, ...response.data.events]
        : response.data.events;
      context.commit('setEvents', { events, total: response.total });
      return events;
    } catch {
      return context.state.events;
    } finally {
      context.commit('setEventsLoading', false);
    }
  },

  async fetchTodayClasses(context, params) {
    try {
      const response = await getClasses(params);
      context.commit('setTodayClasses', response.data.classes);
    } catch {
      context.commit('setTodayClasses', []);
    }
  },

  fetchSchool(context, schoolId) {
    return loadDetail(context, 'setCurrentSchool', async () => (await getSchool(schoolId)).data);
  },

  fetchEvent(context, { eventId, date }) {
    return loadDetail(context, 'setCurrentEvent', async () => (await getEvent(eventId, date)).data.event);
  },

  fetchOrganizer(context, organizerId) {
    return loadDetail(context, 'setCurrentOrganizer', async () => (await getOrganizer(organizerId)).data);
  },

  async reportOutdatedInfo(_, payload) {
    return reportOutdatedInfo(payload);
  },

  /** payload: { targetType: 'school'|'event'|'organizer', targetId, action } */
  track(_, payload) {
    trackInteraction(payload);
  },
};
